import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { MOCK_PRODUCTS, ERP_STAGES } from '../data/mockData';
import { 
  Truck, 
  Package, 
  DoorOpen,
  ChevronLeft,
  Plus,
  CheckCircle2,
  Clock,
  ClipboardList,
  Box,
  FileText,
  ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function DeliveriesModule() {
  const { state, actions } = useApp();
  const { deliveries, quotes, currentDelivery, currentQuote, deliverySelections } = state;
  const [showCreateSheet, setShowCreateSheet] = useState(false);

  // Get active schedules (quotes with status 'active')
  const activeSchedules = quotes.filter(q => q.status === 'active');

  const handleSelectQuote = (quoteId) => {
    actions.setCurrentQuote(quoteId);
  };

  const handleCreateDelivery = (deliveryData) => {
    actions.createDelivery(deliveryData);
    setShowCreateSheet(false);
    toast.success('Delivery created successfully');
  };

  const handleErpAction = (deliveryId, stage) => {
    actions.updateDeliveryErpStage(deliveryId, stage);
    toast.success(`Marked as ${ERP_STAGES.find(s => s.id === stage)?.name}`);
  };

  return (
    <div className="h-full flex flex-col" data-testid="deliveries-module">
      {/* Header */}
      <div className="h-14 border-b border-border flex items-center px-4 justify-between bg-white shrink-0">
        <div className="flex items-center gap-3">
          <Truck className="w-5 h-5 text-primary" />
          <h1 className="font-semibold text-lg">Deliveries</h1>
        </div>
        {currentQuote && (
          <Button onClick={() => setShowCreateSheet(true)} data-testid="create-delivery-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Delivery
          </Button>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Schedule Selection & Deliveries List */}
        <div className="w-80 border-r border-border flex flex-col bg-white shrink-0">
          {/* Schedule Selector */}
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Active Schedule</h3>
            {activeSchedules.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active schedules. Mark a builder as won to activate.</p>
            ) : (
              <div className="space-y-1">
                {activeSchedules.map(schedule => (
                  <div
                    key={schedule.id}
                    className={cn(
                      "p-2 rounded cursor-pointer transition-colors",
                      currentQuote?.id === schedule.id
                        ? "bg-primary/10 border border-primary"
                        : "hover:bg-muted border border-transparent"
                    )}
                    onClick={() => handleSelectQuote(schedule.id)}
                    data-testid={`schedule-select-${schedule.id}`}
                  >
                    <p className="text-sm font-medium">{schedule.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deliveries List */}
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Deliveries {currentQuote && `(${deliveries.filter(d => d.quoteId === currentQuote.id).length})`}
              </h3>
              {currentQuote ? (
                <DeliveriesList
                  deliveries={deliveries.filter(d => d.quoteId === currentQuote.id)}
                  currentDelivery={currentDelivery}
                  onSelect={(id) => actions.setCurrentDelivery(id)}
                />
              ) : (
                <p className="text-sm text-muted-foreground">Select an active schedule first</p>
              )}
            </div>
          </div>
        </div>

        {/* Right: Delivery Detail or Create View */}
        <div className="flex-1 overflow-auto bg-muted/30">
          {currentDelivery ? (
            <DeliveryDetail
              delivery={currentDelivery}
              onErpAction={handleErpAction}
            />
          ) : currentQuote ? (
            <DeliverySelectionView
              quote={currentQuote}
              selections={deliverySelections}
              onToggleSelection={actions.toggleDeliverySelection}
              onClearSelections={actions.clearDeliverySelections}
              onCreateDelivery={() => setShowCreateSheet(true)}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Truck className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground">Select an active schedule to manage deliveries</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Delivery Sheet */}
      <CreateDeliverySheet
        open={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        quote={currentQuote}
        selections={deliverySelections}
        onCreate={handleCreateDelivery}
      />
    </div>
  );
}

function DeliveriesList({ deliveries, currentDelivery, onSelect }) {
  if (deliveries.length === 0) {
    return (
      <div className="text-center py-8">
        <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No deliveries yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {deliveries.map(delivery => (
        <div
          key={delivery.id}
          className={cn(
            "p-3 border rounded-lg cursor-pointer transition-all",
            currentDelivery?.id === delivery.id
              ? "border-primary bg-primary/5"
              : "border-border bg-white hover:border-primary/50"
          )}
          onClick={() => onSelect(delivery.id)}
          data-testid={`delivery-item-${delivery.id}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              DEL-{delivery.id.slice(-6).toUpperCase()}
            </span>
            <ErpStatusBadge status={delivery.status} />
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span>{delivery.totalItems} items</span>
            <span>${delivery.totalCost.toFixed(2)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function DeliveryDetail({ delivery, onErpAction }) {
  const getNextStage = () => {
    const stageOrder = ['allocated', 'picking', 'packing', 'shipped', 'invoiced'];
    const currentIndex = stageOrder.indexOf(delivery.status);
    return stageOrder[currentIndex + 1] || null;
  };

  const nextStage = getNextStage();
  const nextStageConfig = ERP_STAGES.find(s => s.id === nextStage);

  return (
    <div className="p-6" data-testid="delivery-detail">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">DEL-{delivery.id.slice(-6).toUpperCase()}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Created {new Date(delivery.createdAt).toLocaleDateString()}
          </p>
        </div>
        {nextStage && (
          <Button onClick={() => onErpAction(delivery.id, nextStage)} data-testid={`erp-action-${nextStage}`}>
            <ArrowRight className="w-4 h-4 mr-2" />
            Mark as {nextStageConfig?.name}
          </Button>
        )}
      </div>

      {/* ERP Pipeline */}
      <div className="bg-white rounded-lg border border-border p-4 mb-6">
        <h3 className="text-sm font-medium mb-4">ERP Pipeline</h3>
        <div className="flex items-center justify-between">
          {ERP_STAGES.map((stage, idx) => {
            const isCompleted = delivery.erpStages[stage.id]?.completed;
            const isCurrent = delivery.status === stage.id;
            const Icon = getStageIcon(stage.id);

            return (
              <React.Fragment key={stage.id}>
                <div className={cn(
                  "flex flex-col items-center gap-1",
                  isCompleted ? "text-green-600" : isCurrent ? "text-primary" : "text-muted-foreground"
                )}>
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    isCompleted ? "bg-green-100" : isCurrent ? "bg-primary/10" : "bg-muted"
                  )}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs font-medium">{stage.name}</span>
                  {isCompleted && delivery.erpStages[stage.id]?.date && (
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(delivery.erpStages[stage.id].date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {idx < ERP_STAGES.length - 1 && (
                  <div className={cn(
                    "flex-1 h-0.5 mx-2",
                    isCompleted ? "bg-green-300" : "bg-muted"
                  )} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Total Items</p>
          <p className="text-2xl font-semibold">{delivery.totalItems}</p>
        </div>
        <div className="bg-white rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Total Value</p>
          <p className="text-2xl font-semibold">${delivery.totalCost.toFixed(2)}</p>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-lg border border-border">
        <div className="p-4 border-b border-border">
          <h3 className="font-medium">Delivery Items</h3>
        </div>
        <div className="divide-y divide-border">
          {delivery.items.map((item, idx) => {
            const product = MOCK_PRODUCTS.find(p => p.id === item.productId);
            return (
              <div key={idx} className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{product?.name || item.productId}</p>
                  <p className="text-xs text-muted-foreground">
                    For: {item.doors.slice(0, 3).join(', ')}{item.doors.length > 3 && ` +${item.doors.length - 3} more`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm">×{item.quantity}</p>
                  <p className="text-xs text-muted-foreground">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DeliverySelectionView({ quote, selections, onToggleSelection, onClearSelections, onCreateDelivery }) {
  const selectedCount = selections.length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Select Items for Delivery</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Select floors or individual doors to include in a delivery
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <>
              <Button variant="outline" onClick={onClearSelections}>
                Clear ({selectedCount})
              </Button>
              <Button onClick={onCreateDelivery} data-testid="proceed-delivery-btn">
                Proceed to Delivery
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {quote.buildings.map(building => (
          <div key={building.id} className="bg-white rounded-lg border border-border">
            <div className="p-4 border-b border-border">
              <h3 className="font-medium">{building.name}</h3>
            </div>
            <div className="divide-y divide-border">
              {building.floors.map(floor => {
                const isFloorSelected = selections.some(s => s.type === 'floor' && s.id === floor.id);
                const productCount = floor.doors.reduce((sum, d) => sum + d.products.length, 0);

                return (
                  <div key={floor.id} className="p-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isFloorSelected}
                        onCheckedChange={() => onToggleSelection('floor', floor.id, {
                          buildingId: building.id,
                          floorId: floor.id
                        })}
                        data-testid={`select-floor-${floor.id}`}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{floor.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {floor.doors.length} doors • {productCount} products
                        </p>
                      </div>
                    </div>

                    {/* Doors */}
                    <div className="ml-8 mt-2 space-y-1">
                      {floor.doors.map(door => {
                        const isDoorSelected = selections.some(s => s.type === 'door' && s.id === door.id);
                        return (
                          <div key={door.id} className="flex items-center gap-3 py-1">
                            <Checkbox
                              checked={isDoorSelected || isFloorSelected}
                              disabled={isFloorSelected}
                              onCheckedChange={() => onToggleSelection('door', door.id, {
                                buildingId: building.id,
                                floorId: floor.id,
                                doorId: door.id
                              })}
                              data-testid={`select-door-${door.id}`}
                            />
                            <DoorOpen className={cn(
                              "w-4 h-4",
                              door.type === 'exit' && "text-green-600",
                              door.type === 'fire-rated' && "text-red-500",
                              door.type === 'standard' && "text-slate-500"
                            )} />
                            <span className="text-sm">{door.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {door.products.length} products
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateDeliverySheet({ open, onClose, quote, selections, onCreate }) {
  const deliveryData = useMemo(() => {
    if (!quote) return { doors: [], products: {}, totalItems: 0, totalCost: 0 };

    const doors = [];
    const products = {};

    selections.forEach(selection => {
      if (selection.type === 'floor') {
        const building = quote.buildings.find(b => b.id === selection.path.buildingId);
        const floor = building?.floors.find(f => f.id === selection.path.floorId);
        if (floor) {
          floor.doors.forEach(door => {
            doors.push({ ...door, floorName: floor.name, buildingName: building.name });
          });
        }
      } else if (selection.type === 'door') {
        const building = quote.buildings.find(b => b.id === selection.path.buildingId);
        const floor = building?.floors.find(f => f.id === selection.path.floorId);
        const door = floor?.doors.find(d => d.id === selection.path.doorId);
        if (door && !doors.find(d => d.id === door.id)) {
          doors.push({ ...door, floorName: floor.name, buildingName: building.name });
        }
      }
    });

    doors.forEach(door => {
      door.products.forEach(productId => {
        if (products[productId]) {
          products[productId].quantity += 1;
          products[productId].doors.push(door.name);
        } else {
          const product = MOCK_PRODUCTS.find(p => p.id === productId);
          if (product) {
            products[productId] = {
              productId,
              name: product.name,
              price: product.price,
              quantity: 1,
              doors: [door.name]
            };
          }
        }
      });
    });

    const totalItems = Object.values(products).reduce((sum, p) => sum + p.quantity, 0);
    const totalCost = Object.values(products).reduce((sum, p) => sum + (p.price * p.quantity), 0);

    return { doors, products, totalItems, totalCost };
  }, [quote, selections]);

  const handleCreate = () => {
    if (deliveryData.totalItems === 0) {
      toast.error('No products to deliver');
      return;
    }
    onCreate({
      quoteId: quote.id,
      items: Object.values(deliveryData.products),
      totalItems: deliveryData.totalItems,
      totalCost: deliveryData.totalCost
    });
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:w-[640px]" data-testid="create-delivery-sheet">
        <SheetHeader>
          <SheetTitle>Create Delivery</SheetTitle>
          <SheetDescription>Review and confirm delivery details</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Doors</p>
              <p className="text-xl font-semibold">{deliveryData.doors.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Items</p>
              <p className="text-xl font-semibold">{deliveryData.totalItems}</p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-semibold text-primary">${deliveryData.totalCost.toFixed(2)}</p>
            </div>
          </div>

          {/* Products */}
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {Object.values(deliveryData.products).map(product => (
                <div key={product.productId} className="p-3 bg-white border rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{product.name}</span>
                    <span className="text-sm">×{product.quantity}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    ${(product.price * product.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate} disabled={deliveryData.totalItems === 0} data-testid="confirm-delivery-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Create Delivery
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ErpStatusBadge({ status }) {
  const stage = ERP_STAGES.find(s => s.id === status);
  const isComplete = status === 'invoiced';
  
  return (
    <Badge variant={isComplete ? "default" : "outline"} className={cn(
      isComplete && "bg-green-100 text-green-700"
    )}>
      {stage?.name || status}
    </Badge>
  );
}

function getStageIcon(stageId) {
  const icons = {
    allocated: Package,
    picking: ClipboardList,
    packing: Box,
    shipped: Truck,
    invoiced: FileText
  };
  return icons[stageId] || Package;
}
